import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Home,
    Briefcase,
    BookOpen,
    MessageCircle,
    Menu,
    X,
    LayoutDashboard,
    HelpCircle,
    Mail,
    Video,
    Shield,
    FileText,
    LogIn,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useAuth } from "@/contexts/AuthContext";
import { trackContactClick } from "@/lib/analytics";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import LanguageToggle from "./LanguageToggle";

const WHATSAPP_NUMBER = "963992367582";

interface TabItemProps {
    to: string;
    icon: React.ReactNode;
    label: string;
    active: boolean;
    onClick?: () => void;
}

const TabItem = ({ to, icon, label, active, onClick }: TabItemProps) => (
    <Link
        to={to}
        onClick={onClick}
        className={cn(
            "flex flex-col items-center justify-center flex-1 min-w-0 h-full gap-0.5 transition-colors",
            active ? "text-primary" : "text-white/60 active:text-white",
        )}
        aria-current={active ? "page" : undefined}
    >
        <span
            className={cn(
                "flex items-center justify-center w-10 h-7 rounded-full transition-colors",
                active && "bg-primary/15",
            )}
        >
            {icon}
        </span>
        <span className="text-[10px] font-bold leading-none tracking-wide truncate max-w-full px-1">
            {label}
        </span>
    </Link>
);

const MobileTabBar = () => {
    const { t } = useTranslation();
    const { dir, lang } = useLanguage();
    const localizedPath = useLocalizedPath();
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const [sheetOpen, setSheetOpen] = useState(false);

    const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("common.whatsapp_initial_inquiry"),
    )}`;

    const isActive = (path: string) => {
        const localized = localizedPath(path);
        if (path === "/") return location.pathname === localized;
        return location.pathname === localized || location.pathname.startsWith(`${localized}/`);
    };

    const moreLabel = lang === "ar" ? "المزيد" : "More";
    const closeSheet = () => setSheetOpen(false);

    const moreLinks = [
        { to: localizedPath("/about"), icon: <Briefcase size={18} />, label: t("nav.about") },
        { to: localizedPath("/videos"), icon: <Video size={18} />, label: t("nav.videos") },
        { to: localizedPath("/contact"), icon: <Mail size={18} />, label: t("nav.contact") },
        { to: localizedPath("/faq"), icon: <HelpCircle size={18} />, label: t("nav.faq") },
        { to: localizedPath("/privacy"), icon: <Shield size={18} />, label: t("nav.privacy") },
        { to: localizedPath("/terms"), icon: <FileText size={18} />, label: t("nav.terms") },
    ];

    return (
        <nav
            dir={dir}
            aria-label={t("common.main_nav")}
            className={cn(
                "lg:hidden fixed inset-x-0 bottom-0 z-40",
                "bg-background/85 border-t border-white/10",
                "[backdrop-filter:blur(18px)] [-webkit-backdrop-filter:blur(18px)]",
                "pb-[max(0.25rem,env(safe-area-inset-bottom))]",
            )}
        >
            <div className="relative flex items-stretch h-16 px-1">
                <TabItem
                    to={localizedPath("/")}
                    icon={<Home size={20} strokeWidth={2.2} />}
                    label={t("nav.home")}
                    active={isActive("/")}
                />
                <TabItem
                    to={localizedPath("/portfolio")}
                    icon={<Briefcase size={20} strokeWidth={2.2} />}
                    label={t("nav.portfolio")}
                    active={isActive("/portfolio")}
                />

                {/* Center WhatsApp FAB — raised above the bar */}
                <div className="flex-1 min-w-0 flex items-start justify-center -mt-5">
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackContactClick("whatsapp_sy", "mobile_tab_bar")}
                        aria-label={t("common.whatsapp")}
                        className={cn(
                            "w-14 h-14 rounded-full flex items-center justify-center",
                            "bg-[#128C7E] active:bg-[#075E54] text-white",
                            "shadow-[0_8px_24px_-6px_rgba(18,140,126,0.6)]",
                            "border-4 border-background",
                            "transition-transform active:scale-95",
                        )}
                    >
                        <MessageCircle size={24} strokeWidth={2.2} />
                    </a>
                </div>

                <TabItem
                    to={localizedPath("/blog")}
                    icon={<BookOpen size={20} strokeWidth={2.2} />}
                    label={t("nav.blog")}
                    active={isActive("/blog")}
                />

                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <SheetTrigger asChild>
                        <button
                            type="button"
                            className={cn(
                                "flex flex-col items-center justify-center flex-1 min-w-0 h-full gap-0.5 transition-colors",
                                "text-white/60 active:text-white",
                            )}
                            aria-label={moreLabel}
                        >
                            <span className="flex items-center justify-center w-10 h-7 rounded-full">
                                <Menu size={20} strokeWidth={2.2} />
                            </span>
                            <span className="text-[10px] font-bold leading-none tracking-wide truncate max-w-full px-1">
                                {moreLabel}
                            </span>
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="bottom"
                        dir={dir}
                        className={cn(
                            "rounded-t-3xl border-t border-white/10 bg-background/95",
                            "[backdrop-filter:blur(24px)] [-webkit-backdrop-filter:blur(24px)]",
                            "max-h-[85svh] overflow-y-auto",
                            "pb-[max(1.5rem,env(safe-area-inset-bottom))]",
                        )}
                    >
                        {/* Drag handle */}
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/20" />

                        <div className="pt-4 pb-2 grid grid-cols-3 gap-2">
                            {moreLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    onClick={closeSheet}
                                    className={cn(
                                        "flex flex-col items-center justify-center gap-2 p-4 rounded-2xl",
                                        "bg-white/[0.04] border border-white/10 active:bg-white/[0.08]",
                                        "text-white",
                                    )}
                                >
                                    <span className="text-primary">{link.icon}</span>
                                    <span className="text-xs font-bold text-center leading-tight">{link.label}</span>
                                </Link>
                            ))}
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                            {isAuthenticated ? (
                                <Link
                                    to="/dashboard"
                                    onClick={closeSheet}
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-sm active:bg-white/10 transition-colors"
                                >
                                    <LayoutDashboard size={18} className="text-primary" />
                                    {t("common.dashboard")}
                                </Link>
                            ) : (
                                <Link
                                    to="/login"
                                    onClick={closeSheet}
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-sm active:bg-white/10 transition-colors"
                                >
                                    <LogIn size={18} className="text-primary" />
                                    {t("common.login")}
                                </Link>
                            )}
                            <LanguageToggle variant="mobile" />
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </nav>
    );
};

export default MobileTabBar;
